import { setCurrentProject } from 'app/project/actions';
import type { GraphQLSchema } from 'graphql';
import produce from 'immer';
import { type ActionType, createAction, createReducer } from 'typesafe-actions';
import { INITIAL_MOCK_CLAIMS_STATE } from './components/query-debugger/mock-claims/mock-claims-form.js';
import type { DebuggerTab } from './components/query-debugger/query-debugger.js';
import type { Tracing } from './components/query-debugger/types.js';

export const NAME = 'apiExplorer';

export type ApiExplorerSlice = {
  [NAME]: ApiExplorerState;
};

export type MockClaims = {
  useMockClaims: boolean;
  mockClaims: Record<string, unknown>;
};

export type Debugger = {
  activeTab: DebuggerTab;
  mockClaims: MockClaims;
};

export type ApiExplorerState = {
  schema: GraphQLSchema | null;
  path: string;
  query?: string;
  variables?: string;
  response?: string;
  tracing?: Tracing;
  debugger: Debugger;
};

export const initialState: ApiExplorerState = {
  schema: null,
  path: 'admin/graphql',
  debugger: {
    activeTab: 'timeline' as DebuggerTab,
    mockClaims: INITIAL_MOCK_CLAIMS_STATE
  }
};

export const setApiSchema = createAction(`${NAME}/SET_API_SCHEMA`)<GraphQLSchema | null>();
export const setApiQuery = createAction(`${NAME}/SET_API_QUERY`)<string | undefined>();
export const setApiVariables = createAction(`${NAME}/SET_API_VARIABLES`)<string | undefined>();
export const setApiResponse = createAction(`${NAME}/SET_API_RESPONSE`)<string | undefined>();
export const setApiTracing = createAction(`${NAME}/SET_API_TRACING`)<Tracing | undefined>();
export const setApiPath = createAction(`${NAME}/SET_API_PATH`)<string>();
export const setDebuggerTab = createAction(`${NAME}/SET_DEBUGGER_TAB`)<DebuggerTab>();
export const setMockClaims = createAction(`${NAME}/SET_MOCK_CLAIMS`)<MockClaims>();

export type PreviewQueryPayload = {
  shapeName?: string;
  queryName?: string;
};

export const previewQuery = createAction(`${NAME}/PREVIEW_QUERY`)<PreviewQueryPayload>();

type ApiExplorerAction =
  | ActionType<typeof setCurrentProject>
  | ActionType<typeof setApiQuery>
  | ActionType<typeof setApiVariables>
  | ActionType<typeof setApiResponse>
  | ActionType<typeof setApiTracing>
  | ActionType<typeof setApiPath>
  | ActionType<typeof setApiSchema>
  | ActionType<typeof previewQuery>
  | ActionType<typeof setDebuggerTab>
  | ActionType<typeof setMockClaims>;

export const reducer = createReducer<ApiExplorerState, ApiExplorerAction>(initialState)
  .handleAction(setCurrentProject, () => initialState)
  .handleAction(setApiQuery, (state, action) => ({
    ...state,
    query: action.payload
  }))
  .handleAction(setApiVariables, (state, action) => ({
    ...state,
    variables: action.payload
  }))
  .handleAction(setApiResponse, (state, action) => ({
    ...state,
    response: action.payload
  }))
  .handleAction(setApiTracing, (state, action) => ({
    ...state,
    tracing: action.payload
  }))
  .handleAction(setApiPath, (state, action) => ({
    ...state,
    path: action.payload
  }))
  .handleAction(setApiSchema, (state, action) => ({
    ...state,
    schema: action.payload
  }))
  .handleAction(setDebuggerTab, (state, action) =>
    produce(state, (draft) => {
      draft.debugger.activeTab = action.payload;
    })
  )
  .handleAction(setMockClaims, (state, action) =>
    produce(state, (draft) => {
      draft.debugger.mockClaims = action.payload;
    })
  );
